/**
 * Printable purchase-order document. Renders a saved PO (its line items) as a clean one-page document
 * you can print and work from — the buy-list you build the S&S cart against, and later the checklist you
 * receive against. Reuses the batch pick-list's print mechanism: a body-level portal (`id="po-print"`)
 * that `@media print` isolates (globals.css hides every other body child), plus `window.print()`. No
 * external print/PDF dependency.
 *
 * Read-only — it fetches `GET /purchase-orders/{poNumber}` (same cost-gated endpoint the panel uses) and
 * shows the lines. The check-off column + "Ordered by / Date" footer make the printout double as a
 * place-order checklist (tick each as you add it to S&S) and a receiving checklist (tick as you unpack).
 */
import { useQuery } from '@tanstack/react-query';
import { Printer, X } from 'lucide-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { blankLabel, variantLabel } from './inventory-lib';
import { money } from './purchase-draft-lib';
import { poStatusMeta } from './purchase-order-lib';

const shortDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

export function PurchaseOrderPrint({
  poNumber,
  onClose,
}: {
  poNumber: string;
  onClose: () => void;
}) {
  const client = useApiClient();
  const query = useQuery({
    queryKey: ['purchase-orders', poNumber],
    queryFn: async () => {
      const { data, error } = await client.GET('/purchase-orders/{poNumber}', {
        params: { path: { poNumber } },
      });
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the purchase order.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const po = query.data;
  const showReceived = (po?.lines ?? []).some((l) => l.receivedQty > 0);

  // Escape closes the overlay (matches the pick-list / app dialog behaviour).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      id="po-print"
      role="dialog"
      aria-modal="true"
      aria-label={`Purchase order ${poNumber}`}
      className="fixed inset-0 z-50 overflow-auto bg-background p-4 sm:p-6 print:static print:overflow-visible print:bg-transparent print:p-0"
    >
      <div className="mx-auto max-w-3xl space-y-5">
        {/* Toolbar — screen only */}
        <div className="flex items-center justify-end gap-2 print:hidden">
          <Button size="sm" variant="outline" onClick={() => window.print()} disabled={!po}>
            <Printer className="size-4" />
            Print
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="size-4" />
            Close
          </Button>
        </div>

        {query.isPending && <Skeleton className="h-64 w-full" />}
        {query.isError && (
          <Alert variant="destructive" className="print:hidden">
            <AlertTitle>Couldn't load the purchase order</AlertTitle>
            <AlertDescription>
              {query.error instanceof Error ? query.error.message : 'Network error.'}
            </AlertDescription>
          </Alert>
        )}

        {po && (
          <>
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Fast Screen Print
                </div>
                <h1 className="text-xl font-semibold">Purchase Order</h1>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-mono text-lg font-bold">{po.poNumber}</span>
                  {po.source === 'manual' && (
                    <span className="rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                      Manual
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <div>
                  Status: <span className="font-medium">{poStatusMeta(po.status).label}</span>
                </div>
                <div>Created: {shortDate(po.createdAt)}</div>
                {/* Print-only date stamp (in case the PO changes after this copy is printed). */}
                <div className="hidden print:block">
                  Printed: {shortDate(new Date().toISOString())}
                </div>
              </div>
            </div>

            {/* S&S reference to paste into the PO field when placing the order */}
            <div className="rounded-md border px-3 py-2 text-sm">
              <span className="text-muted-foreground">S&amp;S PO reference: </span>
              <span className="font-mono font-semibold">{po.poNumber}</span>
              <span className="text-muted-foreground">
                {' '}
                — enter this in S&amp;S's PO field when you place the order.
              </span>
            </div>
            {po.note && <div className="text-sm text-muted-foreground">Note: {po.note}</div>}

            {/* Line items */}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full min-w-[560px] text-sm">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="w-8 px-3 py-2 text-center text-xs font-medium text-muted-foreground">
                      ✓
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      Blank
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
                      Color · Size
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                      Qty
                    </th>
                    {showReceived && (
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                        Received
                      </th>
                    )}
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                      Unit
                    </th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">
                      Line total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {po.lines.map((l) => (
                    <tr key={l.ssSku} className="border-t align-top">
                      <td className="px-3 py-2 text-center">
                        {/* Empty check box — tick as you add it to the S&S cart / unpack it. */}
                        <span className="inline-block size-3.5 rounded-[2px] border border-current align-middle" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{blankLabel(l)}</div>
                        <div className="font-mono text-xs text-muted-foreground">{l.ssSku}</div>
                      </td>
                      <td className="px-3 py-2">{variantLabel(l) || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {l.orderQty}
                      </td>
                      {showReceived && (
                        <td className="px-3 py-2 text-right tabular-nums">{l.receivedQty}</td>
                      )}
                      <td className="px-3 py-2 text-right tabular-nums">{money(l.unitCost)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(l.lineTotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-medium">
                    {/* ✓ column (1), then span Blank + Color·Size (2) so totalUnits lands under Qty and
                        the estimated total under Line total — in BOTH the 6- and 7-column layouts. The
                        optional Received filler <td> keeps the received (7-column) case aligned. */}
                    <td className="px-3 py-2" />
                    <td className="px-3 py-2" colSpan={2}>
                      {po.lineCount} blank{po.lineCount === 1 ? '' : 's'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{po.totalUnits}</td>
                    {showReceived && <td className="px-3 py-2" />}
                    <td className="px-3 py-2 text-right text-muted-foreground">Est. total</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {money(po.estimatedTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="text-xs text-muted-foreground">
              Est. total is the S&amp;S effective price × quantity — S&amp;S shows the final price
              at checkout (a small/broken-case quantity may bill higher).
            </p>

            {/* Handwritten paper trail */}
            <div className="flex flex-wrap gap-x-10 gap-y-2 pt-4 text-sm">
              <div>Ordered by ______________________</div>
              <div>Date ______________</div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
